import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useChatClient, useChatData } from '../context/ChatContext';
import ConversationList from './ConversationList';
import ChatWindow from './ChatWindow';
import EmptyState from './EmptyState';
import NewConversationModal from './NewConversationModal';
import NewGroupModal from './NewGroupModal';
import Preloader from '../../common/preloader/preloader';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../web3/context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faTriangleExclamation, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import '../styles/chat.css';

const ChatDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { hasPassword } = useAuth();
  const { isInitialized, isInitializing, initError, initProgress, chatAddress, initializeChat } = useChatClient();
  const { conversations, activeConversationId, selectConversation } = useChatData();
  const [showNewChat, setShowNewChat] = useState(false);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [password, setPassword] = useState('');

  const activeConversation = useMemo(
    () => conversations.find((c) => c.id === activeConversationId) || null,
    [conversations, activeConversationId]
  );

  const handleSelectConversation = useCallback(
    (conversationId: string | null) => {
      selectConversation(conversationId);
      if (conversationId) {
        setMobileShowChat(true);
      }
    },
    [selectConversation]
  );

  const handleBack = useCallback(() => {
    setMobileShowChat(false);
    selectConversation(null);
  }, [selectConversation]);

  const handleInit = useCallback(() => {
    initializeChat(hasPassword ? password : undefined);
  }, [initializeChat, hasPassword, password]);

  // Clear active conversation when leaving the messages page
  // so the stream correctly increments unread counts
  useEffect(() => {
    return () => {
      selectConversation(null);
    };
  }, [selectConversation]);

  // Not initialized - show init screen
  if (!isInitialized) {
    // Use the site's global Preloader for loading state
    if (isInitializing) {
      return (
        <div className="chat-dashboard">
          <Preloader show={true} />
          {initProgress && (
            <div className="chat-init-progress-text">
              <p>{initProgress}</p>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="chat-dashboard">
        <div className="chat-init-screen">
          <div className="chat-init-screen__content">
            <div className="chat-init-screen__icon">
              <FontAwesomeIcon icon={faLock} />
            </div>
            <h4>{t('chat.enableMessaging', 'Enable Encrypted Messaging')}</h4>
            <p>
              {t(
                'chat.enableDesc',
                'Start secure, end-to-end encrypted conversations with other wallet addresses using XMTP protocol.'
              )}
            </p>
            {!chatAddress && (
              <p className="chat-init-screen__warning">
                <FontAwesomeIcon icon={faTriangleExclamation} />{' '}
                {t('chat.connectEvmWallet', 'Please connect an EVM wallet to use chat.')}
              </p>
            )}
            {hasPassword && (
              <div className="chat-init-screen__password">
                <input
                  type="password"
                  className="chat-search-input"
                  placeholder={t('chat.enterPassword', 'Enter wallet password')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            )}
            {initError && (
              <p className="chat-init-screen__error">
                <FontAwesomeIcon icon={faCircleExclamation} /> {initError}
              </p>
            )}
            <button
              className="chat-btn chat-btn--primary chat-btn--lg"
              onClick={handleInit}
              disabled={!chatAddress}
            >
              {t('chat.enableChat', 'Enable Chat')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-dashboard">
      {/* Conversation list - hidden on mobile when viewing a chat */}
      <div className={`chat-dashboard__sidebar ${mobileShowChat ? 'chat-dashboard__sidebar--hidden-mobile' : ''}`}>
        <ConversationList
          onNewChat={() => setShowNewChat(true)}
          onNewGroup={() => setShowNewGroup(true)}
        />
      </div>

      {/* Chat window */}
      <div className={`chat-dashboard__main ${!mobileShowChat ? 'chat-dashboard__main--hidden-mobile' : ''}`}>
        {activeConversation ? (
          <ChatWindow
            conversation={activeConversation}
            onBack={handleBack}
          />
        ) : (
          <EmptyState />
        )}
      </div>

      {/* Modals */}
      <NewConversationModal
        show={showNewChat}
        onHide={() => setShowNewChat(false)}
      />
      <NewGroupModal
        show={showNewGroup}
        onHide={() => setShowNewGroup(false)}
      />
    </div>
  );
};

export default ChatDashboard;
